/**
 * Keeps the one AudioContext playing across the page's lifecycle (owner, iPhone 15 Pro, Safari and the Home Screen
 * app: "after backgrounding the browser the music stops").
 *
 * What WebKit does to a context when the page leaves the front (app switch, lock screen, a call, Siri, an alarm): it
 * goes "interrupted" (or "suspended"), and coming back it may stay there. A `resume()` outside a tap can be refused or
 * left pending until an interruption end that iOS never delivers, and a context can come back "running" with its clock
 * frozen (no sound at all) or be closed. The old code only tried `resume()` once on `visibilitychange`, and in a tap
 * resumed a context that could no longer run: the bed stayed silent until a reload.
 *
 * The rules here:
 * - hidden (`visibilitychange`, `pagehide`): suspend on purpose (no battery drain in the background);
 * - back (`visibilitychange`, `pageshow` from the back-forward cache, `focus`): resume; if that does not take, arm a
 *   one-shot resume on the next tap or key (capture phase, passive: nothing in the page can swallow it);
 * - an unexpected "interrupted"/"suspended" while visible (a call, Siri): the same resume-or-arm path;
 * - in a tap: a "suspended" context resumes; an "interrupted" one, a closed one, or one a tap already failed to
 *   resume is replaced by a fresh context built inside the tap (the only place iOS lets a new one start);
 * - "running" is checked: if its clock has not moved a little later, the context is replaced (and a tap starts it).
 * The host rebuilds the graph on a new context and restarts the bed where it was (audio.ts, musicBeds.ts).
 */

/** The calls the lifecycle makes on a context: a subset of AudioContext, so the tests can drive a fake. */
export interface LifecycleCtx {
  readonly state: string;
  readonly currentTime: number;
  resume(): Promise<void>;
  suspend(): Promise<void>;
  addEventListener(type: "statechange", listener: () => void): void;
  removeEventListener(type: "statechange", listener: () => void): void;
}

export interface LifecycleHost<C extends LifecycleCtx> {
  /** The context in use, made on first call (it must `watch` it); null where there is no Web Audio. */
  context(): C | null;
  /** Drops `dead` and builds a fresh context with the graph and the bed where it was (it must `watch` the new one). */
  recreate(dead: C): C | null;
  /** The context runs (again): the bed plays, a held player answer sounds. Called once per transition to "running". */
  running(ctx: C): void;
}

type Listener = (e: Event) => void;
interface Target {
  addEventListener(type: string, listener: Listener, options?: AddEventListenerOptions | boolean): void;
  removeEventListener(type: string, listener: Listener, options?: EventListenerOptions | boolean): void;
}

/** The page the lifecycle listens to, injected so the tests run in Node with fakes. */
export interface LifecycleEnv {
  readonly doc: Target & { readonly hidden: boolean };
  readonly win: Target;
  readonly now: () => number;
  readonly setTimer: (fn: () => void, ms: number) => unknown;
  readonly clearTimer: (handle: unknown) => void;
}

export function browserLifecycleEnv(): LifecycleEnv {
  return {
    doc: document,
    win: window,
    now: () => performance.now(),
    setTimer: (fn, ms) => setTimeout(fn, ms),
    clearTimer: (h) => clearTimeout(h as ReturnType<typeof setTimeout>),
  };
}

export class AudioLifecycle<C extends LifecycleCtx> {
  /**
   * The events that arm a resume. WebKit counts touchend/pointerup/click/keydown as the activation that may start
   * audio; pointerdown is there for Chromium and a mouse.
   */
  static readonly GestureEvents = ["pointerdown", "pointerup", "touchend", "click", "keydown"] as const;
  /** A running context's clock is checked this long after it starts (ms)… */
  static readonly StallCheckMs = 1200;
  /** …and must have moved at least this far (s), or it is treated as dead (WebKit's silent "running"). */
  static readonly StallMinAdvanceS = 0.3;
  /** Replacements per return to the front without a context seen running with a moving clock: iOS caps live contexts. */
  static readonly MaxRecreates = 3;
  /**
   * One tap sends pointerdown, pointerup, touchend and click: a resume asked for by the first of them gets this long
   * (ms) to take before a later tap gives up on the context and replaces it.
   */
  static readonly TapGraceMs = 1000;

  private watched: C | null = null;
  /** We suspended it because the page went out of sight: its "suspended" is ours, not an interruption. */
  private hiddenPause = false;
  private armed = false;
  /** When a tap first asked this context to resume (it has not run since); a tap past the grace replaces it. */
  private tapAt: number | null = null;
  private stallTimer: unknown = null;
  private recreates = 0;
  private started = false;

  constructor(private readonly host: LifecycleHost<C>, private readonly env: LifecycleEnv) {}

  /** Installs the page listeners, once. */
  start(): void {
    if (this.started) return;
    this.started = true;
    this.env.doc.addEventListener("visibilitychange", this.onVisibility);
    this.env.win.addEventListener("pagehide", this.onHide);
    this.env.win.addEventListener("pageshow", this.onShow);
    this.env.win.addEventListener("focus", this.onShow);
  }

  /** Follows `ctx`'s state changes (and stops following the one it replaces). Idempotent. */
  watch(ctx: C): void {
    if (this.watched === ctx) return;
    this.watched?.removeEventListener("statechange", this.onState);
    this.watched = ctx;
    this.tapAt = null;
    ctx.addEventListener("statechange", this.onState);
    if (ctx.state === "running") this.verify(ctx);
    else if (this.visible()) this.arm();
  }

  /** Whether the next tap/key is waiting to resume (for the tests and the debug hook). */
  get isArmed(): boolean { return this.armed; }

  /** A user gesture (main.ts's always-on listeners and the armed one-shot): resume, or rebuild inside the gesture. */
  gesture(): void {
    if (!this.visible()) return;
    let ctx = this.host.context();
    if (!ctx) return;
    this.hiddenPause = false;
    if (ctx.state === "running") return;
    // "interrupted" in a tap means WebKit is holding it for an interruption end that may never come; closed is dead;
    // a context an earlier tap could not start will not start now. A new one made here, in the tap, is allowed to start.
    const now = this.env.now();
    const failed = this.tapAt !== null && now - this.tapAt > AudioLifecycle.TapGraceMs;
    if (ctx.state === "closed" || ctx.state === "interrupted" || failed) ctx = this.replace(ctx) ?? ctx;
    this.tapAt ??= now;
    if (ctx.state !== "running" && ctx.state !== "closed") ctx.resume().catch(() => undefined);
  }

  private readonly onVisibility = (): void => { if (this.env.doc.hidden) this.onHide(); else this.wake(); };
  private readonly onShow = (): void => { if (this.visible()) this.wake(); };

  private readonly onHide = (): void => {
    this.hiddenPause = true;
    this.cancelStallCheck();
    const ctx = this.watched;
    if (ctx?.state === "running") ctx.suspend().catch(() => undefined);
  };

  /** Back in front: resume what we suspended, or wait for a tap to. */
  private wake(): void {
    this.hiddenPause = false;
    this.recreates = 0;
    let ctx = this.host.context();
    if (!ctx) return;
    if (ctx.state === "closed") ctx = this.replace(ctx) ?? ctx;
    if (ctx.state === "running") { this.host.running(ctx); this.verify(ctx); return; }
    this.tryResume(ctx);
  }

  private readonly onState = (): void => {
    const ctx = this.watched;
    if (!ctx) return;
    switch (ctx.state) {
      case "running":
        this.disarm();
        this.tapAt = null;
        if (!this.visible()) { this.onHide(); return; }       // resumed on its own while hidden: back to sleep
        this.host.running(ctx);
        this.verify(ctx);
        return;
      case "closed":
        this.cancelStallCheck();
        if (this.visible()) { const fresh = this.replace(ctx); if (fresh) this.tryResume(fresh); }
        return;
      default:                                                // "suspended", or WebKit's "interrupted"
        this.cancelStallCheck();
        if (this.hiddenPause || !this.visible()) return;      // ours, for the background
        this.tryResume(ctx);                                  // a call, Siri, an alarm: take it back, or on the next tap
    }
  };

  private tryResume(ctx: C): void {
    if (ctx.state === "closed") return;
    // Armed first, not after the promise: WebKit may leave it pending for good. Disarmed when it runs.
    this.arm();
    ctx.resume().catch(() => undefined);
  }

  /** WebKit can report "running" with a clock that never moves (and no sound): replace it if so. */
  private verify(ctx: C): void {
    this.cancelStallCheck();
    const from = ctx.currentTime;
    this.stallTimer = this.env.setTimer(() => {
      this.stallTimer = null;
      if (ctx !== this.watched || ctx.state !== "running" || !this.visible()) return;
      if (ctx.currentTime - from >= AudioLifecycle.StallMinAdvanceS) { this.recreates = 0; return; }
      const fresh = this.replace(ctx);
      if (fresh) this.tryResume(fresh);
    }, AudioLifecycle.StallCheckMs);
  }

  private replace(dead: C): C | null {
    if (this.recreates >= AudioLifecycle.MaxRecreates) return null;
    this.recreates++;
    this.cancelStallCheck();
    const fresh = this.host.recreate(dead);
    if (fresh) this.watch(fresh);
    return fresh;
  }

  private readonly onGesture = (): void => this.gesture();

  private arm(): void {
    if (this.armed) return;
    this.armed = true;
    for (const t of AudioLifecycle.GestureEvents) this.env.doc.addEventListener(t, this.onGesture, { capture: true, passive: true });
  }

  private disarm(): void {
    if (!this.armed) return;
    this.armed = false;
    for (const t of AudioLifecycle.GestureEvents) this.env.doc.removeEventListener(t, this.onGesture, { capture: true });
  }

  private cancelStallCheck(): void {
    if (this.stallTimer !== null) this.env.clearTimer(this.stallTimer);
    this.stallTimer = null;
  }

  private visible(): boolean { return !this.env.doc.hidden; }
}
